import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@cms/ui/components/button";
import { Label } from "@cms/ui/components/label";
import { Checkbox } from "@cms/ui/components/checkbox";
import { ColorPicker, type ColorPreset } from "@cms/ui/components/color-picker";
import type {
  SectionAlign,
  SectionColorLayer,
  SectionSettings,
  SectionWidth,
} from "../lib/editor-content";
import { ImageField } from "./image-field";
import { PaddingSlider } from "./padding-slider";
import { Segmented } from "@cms/ui/components/segmented";
import type { ApiClient } from "../lib/api";

function ColorField({ label, value, presets, onChange }: {
  label: string;
  value: string;
  presets: ColorPreset[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <ColorPicker value={value} onChange={onChange} presets={presets} ariaLabel={label} />
      <Label className="text-xs text-muted-foreground">{label}</Label>
    </div>
  );
}

/**
 * The section Properties panel: a slide-in sidebar pinned to the right of the
 * content-editor area (from the bottom of the sticky tabs bar to the bottom of
 * the viewport). A transparent overlay covers the editor to block interaction
 * while it's open; clicking that overlay closes the panel. Every change applies
 * to the section immediately (the canvas is the live preview) — no save step.
 * Content editing lives in the per-column modal instead.
 */
export function SectionPropertiesPanel({ settings, presets, api, anchorRef, onChange, onClose, showColumns = true }: {
  settings: SectionSettings;
  presets: ColorPreset[];
  api: ApiClient;
  /** The sticky tabs bar the sidebar/overlay geometry is derived from. */
  anchorRef: React.RefObject<HTMLElement | null>;
  onChange: (settings: SectionSettings) => void;
  onClose: () => void;
  /** False for section types without free-form columns (timeline). */
  showColumns?: boolean;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  // Geometry derived from the tabs bar: the panel spans from its bottom edge to
  // the bottom of the viewport, across the content-editor column.
  const [box, setBox] = useState<{ top: number; left: number; width: number } | null>(null);
  // Drives the slide-in transition (translateX 100% → 0 after first paint).
  const [entered, setEntered] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setBox({ top: r.bottom, left: r.left, width: r.width });
    };
    measure();
    // The tabs bar is sticky, so its bottom moves as the editor scrolls.
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [anchorRef]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    // Esc closes, unless a portalled dialog (the media picker) is open.
    const dialogOpen = () => document.querySelector('[role="dialog"][data-state="open"]');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !dialogOpen()) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    // The overlay blocks pointer interaction with the editor; also swallow
    // wheel/touch so the content behind can't scroll while the panel is open.
    const el = overlayRef.current;
    if (!el) return undefined;
    const block = (e: Event) => e.preventDefault();
    el.addEventListener("wheel", block, { passive: false });
    el.addEventListener("touchmove", block, { passive: false });
    return () => {
      el.removeEventListener("wheel", block);
      el.removeEventListener("touchmove", block);
    };
  }, [box]);

  const set = (patch: Partial<SectionSettings>) => onChange({ ...settings, ...patch });
  const bg = settings.background;
  const setColorLayer = (color: SectionColorLayer) => set({ background: { ...bg, color } });

  const changeColorType = (type: "none" | "color" | "gradient") => {
    if (type === bg.color.type) return;
    if (type === "none") setColorLayer({ type: "none" });
    else if (type === "color") setColorLayer({ type: "color", color: "#f7f7f7" });
    else setColorLayer({ type: "gradient", from: "#1e3a8a", to: "#2563eb" });
  };

  if (!box) return null;

  return (
    <>
      {/* Transparent interaction blocker over the editor; click closes. */}
      <div
        ref={overlayRef}
        aria-hidden="true"
        className="fixed bottom-0 z-30"
        style={{ top: box.top, left: box.left, width: box.width }}
        onClick={onClose}
      />

      <aside
        className="fixed bottom-0 right-0 z-40 flex w-80 flex-col border-l bg-background text-left shadow-xl transition-transform duration-200 ease-out"
        style={{ top: box.top, transform: entered ? "translateX(0)" : "translateX(100%)" }}
        role="region"
        aria-label="Section properties"
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold">Section properties</p>
          <Button type="button" variant="ghost" size="sm" onClick={onClose} aria-label="Close properties">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
      {showColumns && (
        <div className="space-y-1.5">
          <Label>Columns</Label>
          <Segmented
            value={settings.columns}
            options={[{ value: 1, label: "1" }, { value: 2, label: "2" }, { value: 3, label: "3" }]}
            onChange={(count) => set({ columns: count as 1 | 2 | 3 })}
          />
        </div>
      )}

      <div className="space-y-1.5">
        <Label>Background colour</Label>
        <Segmented
          value={bg.color.type}
          options={[
            { value: "none", label: "None" },
            { value: "color", label: "Colour" },
            { value: "gradient", label: "Gradient" },
          ]}
          onChange={changeColorType}
        />
        {bg.color.type === "color" && (
          <div className="pt-1">
            <ColorField
              label={bg.imageKey ? "Overlays the image — use opacity" : "Background colour"}
              value={bg.color.color}
              presets={presets}
              onChange={(color) => setColorLayer({ type: "color", color })}
            />
          </div>
        )}
        {bg.color.type === "gradient" && (
          <div className="flex flex-wrap gap-3 pt-1">
            <ColorField
              label="From"
              value={bg.color.from}
              presets={presets}
              onChange={(from) => setColorLayer({ ...bg.color, type: "gradient", from } as SectionColorLayer)}
            />
            <ColorField
              label="To"
              value={bg.color.to}
              presets={presets}
              onChange={(to) => setColorLayer({ ...bg.color, type: "gradient", to } as SectionColorLayer)}
            />
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Background image</Label>
        <ImageField
          api={api}
          value={bg.imageKey ? { key: bg.imageKey, url: bg.imageUrl ?? null } : null}
          onChange={(item) =>
            set({
              background: item
                ? { ...bg, imageKey: item.key, imageUrl: item.url ?? undefined }
                : { color: bg.color, imageKey: "" },
            })
          }
          emptyLabel="No image"
        />
        {bg.imageKey && (
          <label className="flex items-center gap-2 pt-1 text-sm">
            <Checkbox
              checked={bg.fixed === true}
              onChange={(e) =>
                set({
                  background: e.target.checked
                    ? { ...bg, fixed: true }
                    : { ...bg, fixed: undefined },
                })
              }
            />
            Fixed background (parallax effect)
          </label>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Content width</Label>
        <Segmented<SectionWidth>
          value={settings.width}
          options={[
            { value: "narrow", label: "Narrow" },
            { value: "normal", label: "Normal" },
            { value: "wide", label: "Wide" },
          ]}
          onChange={(width) => set({ width })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Content alignment</Label>
        <Segmented<SectionAlign>
          value={settings.align}
          options={[
            { value: "left", label: "Left" },
            { value: "center", label: "Centre" },
            { value: "right", label: "Right" },
          ]}
          onChange={(align) => set({ align })}
        />
      </div>

      <div className="space-y-1.5">
        <Label>Padding</Label>
        <PaddingSlider value={settings.padding} onChange={(padding) => set({ padding })} />
        <p className="text-xs text-muted-foreground">Vertical space above and below the content, in pixels.</p>
      </div>

      <label className="flex cursor-pointer items-center justify-between gap-2">
        <span className="text-sm font-medium">Full height</span>
        <Checkbox
          checked={settings.fullHeight}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set({ fullHeight: e.target.checked })}
        />
      </label>
      <p className="-mt-3 text-xs text-muted-foreground">
        Stretch the section to fill the screen, with the content centred vertically.
      </p>
        </div>
      </aside>
    </>
  );
}
